'use client';

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { isNil } from 'es-toolkit';

import { useClientsUrlState, useClientsUrlStateDispatch } from '../utils/url-state-hooks';
import { createInitialState, paginationReducer } from './clients-pagination-reducer';

type ClientsPaginationState = {
  readonly pageSize: number;
  readonly currentPage: number;
  readonly totalItems: number;
  readonly startCursor: string | null;
  readonly endCursor: string | null;
  readonly hasNextPage: boolean;
  readonly hasPreviousPage: boolean;
  readonly currentAfter: string | null;
  readonly currentBefore: string | null;
  readonly currentFirst: number | null;
  readonly currentLast: number | null;
};

type ClientsPaginationActions = {
  readonly setPageSize: (pageSize: number) => void;
  readonly setCurrentPage: (page: number) => void;
  readonly setTotalItems: (total: number) => void;
  readonly setCursors: (cursors: {
    startCursor: string | null;
    endCursor: string | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  }) => void;
  readonly goToNextPage: () => void;
  readonly goToPreviousPage: () => void;
  readonly resetPagination: () => void;
};

type ClientsPaginationContextValue = ClientsPaginationState & ClientsPaginationActions;

const ClientsPaginationContext = createContext<ClientsPaginationContextValue | null>(null);

type ClientsPaginationProviderProps = {
  readonly children: ReactNode;
  readonly initialPageSize?: number;
};

export function ClientsPaginationProvider({ children, initialPageSize = 25 }: ClientsPaginationProviderProps) {
  const urlState = useClientsUrlState();
  const urlDispatch = useClientsUrlStateDispatch();

  const urlPageSize = urlState.pageSize ?? initialPageSize;
  const urlAfter = urlState.after ?? null;
  const urlBefore = urlState.before ?? null;
  const urlCurrentPage = urlState.currentPage ?? null;

  const [paginationState, dispatch] = useReducer(
    paginationReducer,
    createInitialState(urlPageSize, urlCurrentPage ?? undefined, urlAfter, urlBefore),
  );
  const [totalItems, setTotalItems] = useState(0);
  const previousUrlState = useRef({
    after: urlAfter,
    before: urlBefore,
    pageSize: urlPageSize,
    currentPage: urlCurrentPage,
  });
  const hasInitializedPageSize = useRef(false);

  useEffect(() => {
    if (!hasInitializedPageSize.current && isNil(urlState.pageSize)) {
      hasInitializedPageSize.current = true;
      previousUrlState.current = {
        after: paginationState.currentAfter,
        before: paginationState.currentBefore,
        pageSize: paginationState.pageSize,
        currentPage: paginationState.currentPage,
      };
      urlDispatch({
        type: 'setPagination',
        after: paginationState.currentAfter,
        before: paginationState.currentBefore,
        pageSize: paginationState.pageSize,
        currentPage: paginationState.currentPage,
      });
    } else if (!hasInitializedPageSize.current) {
      hasInitializedPageSize.current = true;
    }
  }, [
    urlState.pageSize,
    paginationState.currentAfter,
    paginationState.currentBefore,
    paginationState.pageSize,
    paginationState.currentPage,
    urlDispatch,
  ]);

  useEffect(() => {
    const urlChanged =
      urlAfter !== previousUrlState.current.after ||
      urlBefore !== previousUrlState.current.before ||
      urlPageSize !== previousUrlState.current.pageSize ||
      urlCurrentPage !== previousUrlState.current.currentPage;

    if (urlChanged) {
      const effectiveCurrentPage =
        isNil(urlAfter) && isNil(urlBefore) && !isNil(urlCurrentPage) && urlCurrentPage > 1 ? 1 : urlCurrentPage;

      previousUrlState.current = {
        after: urlAfter,
        before: urlBefore,
        pageSize: urlPageSize,
        currentPage: effectiveCurrentPage,
      };
      dispatch({
        type: 'SET_PAGINATION_FROM_URL',
        payload: {
          after: urlAfter,
          before: urlBefore,
          pageSize: urlPageSize,
          currentPage: effectiveCurrentPage,
        },
      });
    }
  }, [urlAfter, urlBefore, urlPageSize, urlCurrentPage]);

  useEffect(() => {
    const isUserInteraction =
      previousUrlState.current.after === urlAfter &&
      previousUrlState.current.before === urlBefore &&
      previousUrlState.current.pageSize === urlPageSize &&
      previousUrlState.current.currentPage === urlCurrentPage;

    const stateChanged =
      paginationState.currentAfter !== urlAfter ||
      paginationState.currentBefore !== urlBefore ||
      paginationState.pageSize !== urlPageSize ||
      paginationState.currentPage !== urlCurrentPage;

    if (stateChanged && isUserInteraction) {
      previousUrlState.current = {
        after: paginationState.currentAfter,
        before: paginationState.currentBefore,
        pageSize: paginationState.pageSize,
        currentPage: paginationState.currentPage,
      };
      urlDispatch({
        type: 'setPagination',
        after: paginationState.currentAfter,
        before: paginationState.currentBefore,
        pageSize: paginationState.pageSize,
        currentPage: paginationState.currentPage,
      });
    }
  }, [
    paginationState.currentAfter,
    paginationState.currentBefore,
    paginationState.pageSize,
    paginationState.currentPage,
    urlAfter,
    urlBefore,
    urlPageSize,
    urlCurrentPage,
    urlDispatch,
  ]);

  const resetPagination = useCallback(() => {
    dispatch({ type: 'RESET', payload: paginationState.pageSize });
    urlDispatch({
      type: 'setPagination',
      after: null,
      before: null,
      pageSize: paginationState.pageSize,
      currentPage: 1,
    });
  }, [paginationState.pageSize, urlDispatch]);

  const setCursors = useCallback(
    (cursors: {
      startCursor: string | null;
      endCursor: string | null;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    }) => {
      dispatch({ type: 'SET_CURSORS', payload: cursors });
    },
    [],
  );

  const goToNextPage = useCallback(() => {
    dispatch({ type: 'GO_TO_NEXT_PAGE' });
  }, []);

  const goToPreviousPage = useCallback(() => {
    dispatch({ type: 'GO_TO_PREVIOUS_PAGE' });
  }, []);

  const setPageSizeWithReset = useCallback((newPageSize: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: newPageSize });
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
  }, []);

  const value = useMemo<ClientsPaginationContextValue>(
    () => ({
      ...paginationState,
      totalItems,
      setPageSize: setPageSizeWithReset,
      setCurrentPage,
      setTotalItems,
      setCursors,
      goToNextPage,
      goToPreviousPage,
      resetPagination,
    }),
    [
      paginationState,
      totalItems,
      setPageSizeWithReset,
      setCurrentPage,
      setCursors,
      goToNextPage,
      goToPreviousPage,
      resetPagination,
    ],
  );

  return <ClientsPaginationContext.Provider value={value}>{children}</ClientsPaginationContext.Provider>;
}

export function useClientsPagination() {
  const context = useContext(ClientsPaginationContext);
  if (isNil(context)) {
    throw new Error('useClientsPagination must be used within ClientsPaginationProvider');
  }
  return context;
}
